import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  id: string;
  username: string;
  email?: string;
  role: string;
  displayName?: string;
  source: string;
}

export interface AuthState {
  loading: boolean;
  authenticated: boolean;
  user: AuthUser | null;
  loginUrl: string;
}

export const TOKEN_STORAGE_KEY = 'wt_auth_token';

/**
 * Retrieves the active auth token from URL parameters (SSO handoff) or localStorage
 */
export function getStoredAuthToken(): string | null {
  if (typeof window === 'undefined') return null;

  // 1. Check URL parameter first (e.g. ?session=TOKEN from authLion SSO redirect)
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get('session');
    if (sessionToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, sessionToken);
      const url = new URL(window.location.href);
      url.searchParams.delete('session');
      window.history.replaceState({}, '', url.toString());
      return sessionToken;
    }
  } catch {
    // URLSearchParams or history API error ignored
  }

  // 2. Check localStorage
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears stored authentication token
 */
export function clearStoredAuthToken(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // LocalStorage access restricted
  }
}

/**
 * Returns authentication headers including Bearer token if available
 */
export function getAuthHeaders(): HeadersInit {
  const token = getStoredAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export function useSecurityAuth() {
  const [state, setState] = useState<AuthState>({
    loading: true,
    authenticated: false,
    user: null,
    loginUrl: 'https://login.weretrade.com/login?redirect=' + encodeURIComponent(typeof window !== 'undefined' ? window.location.origin : '')
  });

  const checkAuth = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        headers
      });

      if (res.ok) {
        const data = await res.json();
        setState({
          loading: false,
          authenticated: true,
          user: data.user,
          loginUrl: ''
        });
      } else {
        clearStoredAuthToken();
        const data = await res.json().catch(() => ({}));
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        setState({
          loading: false,
          authenticated: false,
          user: null,
          loginUrl: data.loginUrl || (`https://login.weretrade.com/login?redirect=${encodeURIComponent(origin)}`)
        });
      }
    } catch {
      clearStoredAuthToken();
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      setState({
        loading: false,
        authenticated: false,
        user: null,
        loginUrl: `https://login.weretrade.com/login?redirect=${encodeURIComponent(origin)}`
      });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const target = state.loginUrl || `https://login.weretrade.com/login?redirect=${encodeURIComponent(origin)}`;
    window.location.href = target;
  };

  const logout = () => {
    clearStoredAuthToken();
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    window.location.href = `https://login.weretrade.com/api/auth/logout?redirect=${encodeURIComponent(origin)}`;
  };

  return { ...state, login, logout, refresh: checkAuth };
}
